import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProjectsRestDataProvider, Project as ApiProject } from '../../../data/ProjectsRestDataProvider';
import FormGenerator, { FormFieldConfig } from '../../common/FormGenerator/FormGenerator';
import GenericFlyoutPanel from '../../common/GenericFlyoutPanel/GenericFlyoutPanel';
import DetailedListComponent, { DetailedListColumn } from '../../common/DetailedList/DetailedListComponent';
import MainScreenHeader from '../../common/Header/MainScreenHeader';
import './Projects.scss';
import '../../../styles/GenericSiteStyles.scss';
import { UserRoles } from '../../../constants/UserRoles';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

export const getProjectFields = (t: TFunction): FormFieldConfig[] => [
  { key: 'name', label: t('screens:projects.fields.projectName'), type: 'string', required: true },
  { key: 'description', label: t('common:description'), type: 'string', multiline: true, rows: 3 },
  { key: 'status', label: t('common:status'), type: 'select', options: [
    { label: t('screens:projects.statuses.active'), value: 'Active' },
    { label: t('screens:projects.statuses.planning'), value: 'Planning' },
    { label: t('screens:projects.statuses.completed'), value: 'Completed' },
  ], required: true },
  { key: 'startDate', label: t('screens:projects.fields.startDate'), type: 'date' },
  { key: 'endDate', label: t('screens:projects.fields.endDate'), type: 'date' },
  { key: 'budget', label: t('screens:projects.fields.budget'), type: 'number', min: 0 },
  { key: 'isFixedPrice', label: t('screens:projects.fields.isFixedPrice'), type: 'checkbox' },
  { key: 'fixedPriceContingencyPercentage', label: t('screens:projects.fields.contingency'), type: 'number', min: 0, max: 100 },
  { key: 'vatPercentage', label: t('screens:projects.fields.vat'), type: 'number', min: 0, max: 100 },
];

const Projects: React.FC = () => {
  const { t } = useTranslation(['common', 'screens']);
  const [projects, setProjects] = useState<ApiProject[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const fields = useMemo(() => getProjectFields(t), [t]);
  const provider = React.useMemo(() => new ProjectsRestDataProvider(), []);

  React.useEffect(() => {
    setLoading(true);
    provider.getAll()
      .then(setProjects)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [provider]);

  const columns: DetailedListColumn<ApiProject>[] = useMemo(() => [
    { key: 'name', header: t('screens:projects.fields.projectName'), type: 'string', width:200 },
    {
      key: 'status', header: t('common:status'), type: 'string', fixedWidth: true, width: 120,
      render: item => (
        <span className={`badge bg-${item.status === 'Active' ? 'primary' : item.status === 'Planning' ? 'warning text-dark' : 'success'}`}>
          {item.status}
        </span>
      ),
    },
    { key: 'startDate', header: t('screens:projects.fields.startDate'), type: 'date', width: 130, fixedWidth: true },
    { key: 'endDate', header: t('screens:projects.fields.endDate'), type: 'date', width: 130, fixedWidth: true },
    { key: 'budget', header: t('screens:projects.fields.budget'), type: 'number', width: 160, fixedWidth: true },
  ], [t]);

  const handleChange = async (_items: ApiProject[], action: 'add' | 'edit' | 'delete', changedItem: ApiProject) => {
    if (action === 'delete' && changedItem.id) {
      try {
        await provider.delete(changedItem.id);
      } catch (e: any) {
        setError(e.message);
        // Revert the optimistic removal by reloading from the server
        provider.getAll().then(setProjects);
      }
    }
  };

  return (
    <section className="projects-page">
      <MainScreenHeader title={t('screens:projects.title')} />
      {error && <div style={{ color: 'red', marginBottom: 8 }}>{error}</div>}
      {loading && <div>{t('common:loading')}</div>}
      <DetailedListComponent<ApiProject>
        columns={columns}
        dataSource={{ data: projects, setData: setProjects }}
        rowKey={item => item.id ?? ''}
        formFields={fields}
        addRole={`${UserRoles.Admin},${UserRoles.Manager}`}
        editRole=""
        deleteRole={`${UserRoles.Admin},${UserRoles.Manager}`}
        onEditRequest={item => navigate(`/projects/${item.id}`)}
        onAddRequest={() => setShowCreate(true)}
        onChange={handleChange}
        actionsColumnWidth={100}
        actionsColumnFixed
      />
      {showCreate && (
        <GenericFlyoutPanel open={showCreate} onClose={() => setShowCreate(false)} title={t('screens:projects.createNewProject')}>
          <FormGenerator
            fields={fields}
            initialData={{ name: '', status: '', description: '' }}
            onSubmit={async data => {
              setLoading(true);
              setError(null);
              try {
                const { name, status, description } = data;
                const created = await provider.create({ name, status, description });
                setProjects(prev => [...prev, created]);
                setShowCreate(false);
              } catch (e: any) {
                setError(e.message);
              } finally {
                setLoading(false);
              }
            }}
            submitLabel={t('common:create')}
          />
          {error && <div style={{ color: 'red', marginTop: 8 }}>{error}</div>}
        </GenericFlyoutPanel>
      )}
    </section>
  );
};

export default Projects;
